import React, { useLayoutEffect, useRef } from "react";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { Badge } from "../../../ui/Badge";
import {
  CRITERION_PREFIXES,
  MAX_CRITERIA_LENGTH,
  PREFIX_HINT,
  SCORE_TYPE_HINT,
  SCORE_TYPE_LABEL,
  composeCriteria,
  scoreTypeFor,
  type CriterionPrefix,
} from "../utils/criteria";

interface CriteriaEditorProps {
  prefix: CriterionPrefix | null;
  body: string;
  onPrefixChange: (prefix: CriterionPrefix) => void;
  onBodyChange: (body: string) => void;
  disabled?: boolean;
  autoFocus?: boolean;
  /** Rendered under the field — used for the "no criterion is valid yet" hint. */
  error?: string | null;
}

/** Four lines of body text before the field starts growing. */
const MIN_BODY_HEIGHT = 104;

/**
 * The criterion editor: an explicit score-type control plus the body text.
 *
 * The type used to be invisible. It is inferred from the opening words of the criteria
 * string, so whether an evaluator produced a pass/fail judgement or a 0–1 score depended
 * entirely on the first word someone happened to type, and nothing on screen said so.
 * Worse, the platform would happily save a criterion with no recognised prefix at all —
 * the same string the SDK rejects with a 422 — which then scored as a reward metric by
 * accident.
 *
 * Splitting the criterion into a chosen prefix and a free-text body makes the type a
 * deliberate decision and guarantees whatever we send carries a valid prefix.
 */
export function CriteriaEditor({
  prefix,
  body,
  onPrefixChange,
  onBodyChange,
  disabled = false,
  autoFocus = false,
  error = null,
}: CriteriaEditorProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Grow to fit the content instead of stranding two sentences in a half-screen box.
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.max(el.scrollHeight, MIN_BODY_HEIGHT)}px`;
  }, [body]);

  const composed = composeCriteria(prefix, body);
  const length = composed.length;
  const overLimit = length > MAX_CRITERIA_LENGTH;
  const scoreType = prefix ? scoreTypeFor(prefix) : null;
  const hasBody = body.trim().length > 0;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-1.5">
        <span
          className="text-sm font-medium text-foreground"
          id="criteria-type-label"
        >
          How it scores
        </span>
        <div
          className="flex flex-wrap gap-1.5"
          role="radiogroup"
          aria-labelledby="criteria-type-label"
        >
          {CRITERION_PREFIXES.map((option) => {
            const isSelected = option === prefix;
            return (
              <button
                key={option}
                type="button"
                role="radio"
                aria-checked={isSelected}
                title={PREFIX_HINT[option]}
                disabled={disabled}
                onClick={() => onPrefixChange(option)}
                className={[
                  "rounded-md border px-3 h-8 text-xs font-medium transition-colors",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  "disabled:pointer-events-none disabled:opacity-50",
                  isSelected
                    ? "border-primary bg-primary text-primary-foreground shadow-sm"
                    : "border-input bg-card text-foreground hover:bg-accent",
                ].join(" ")}
              >
                {option}
              </button>
            );
          })}
        </div>
      </div>

      {scoreType ? (
        <p className="text-xs text-muted-foreground flex items-center gap-2">
          <Badge variant="outline">{SCORE_TYPE_LABEL[scoreType]}</Badge>
          {SCORE_TYPE_HINT[scoreType]}
        </p>
      ) : hasBody ? (
        // Only a warning when there is existing wording that failed to match — i.e. a
        // criterion written before this control existed. An untouched new form has
        // nothing wrong with it yet.
        <div className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 px-2.5 py-2 text-xs text-foreground">
          <ExclamationTriangleIcon className="w-4 h-4 shrink-0 text-warning mt-px" />
          <span>
            This criterion doesn&apos;t start with a recognised prefix, so its score type
            is ambiguous. Choose one above — your existing wording is kept below.
          </span>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">
          Pick one to set the score type.
        </p>
      )}

      <label
        className="text-sm font-medium text-foreground mt-1"
        htmlFor="criteria-body"
      >
        Criterion
      </label>
      <div
        className={[
          "rounded-md border bg-card transition-colors",
          "focus-within:ring-2 focus-within:ring-ring",
          overLimit ? "border-destructive" : "border-input",
        ].join(" ")}
      >
        {prefix && (
          <div className="px-3 pt-2.5">
            <span className="inline-flex items-center rounded bg-muted px-1.5 py-0.5 font-mono text-xs font-medium text-muted-foreground">
              {prefix}
            </span>
          </div>
        )}
        <textarea
          id="criteria-body"
          ref={textareaRef}
          value={body}
          disabled={disabled}
          autoFocus={autoFocus}
          onChange={(e) => onBodyChange(e.target.value)}
          rows={4}
          style={{ minHeight: MIN_BODY_HEIGHT }}
          placeholder="responses that cite a source for every factual claim"
          className="block w-full resize-none bg-transparent px-3 py-2 font-mono text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none disabled:opacity-50"
        />
      </div>

      <div className="flex items-start justify-between gap-4">
        <p
          className={`text-xs ${error ? "text-destructive" : "text-muted-foreground"}`}
        >
          {error ??
            (prefix
              ? `Saved as “${prefix} …”. The opening words decide the score type.`
              : " ")}
        </p>
        <span
          className={`text-xs tabular-nums shrink-0 ${
            overLimit ? "text-destructive font-medium" : "text-muted-foreground"
          }`}
        >
          {length.toLocaleString()} / {MAX_CRITERIA_LENGTH.toLocaleString()}
        </span>
      </div>
    </div>
  );
}
